/**
 * HealthDashboard - System Health Dashboard
 * Migrated from src/app/components/admin/AdminHealthDashboard.jsx
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import { API_ENDPOINTS } from '../config/api-endpoints';

const { DashboardSkeleton } = SkeletonLoaderComponents;

const HealthDashboard = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHealth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.SYSTEM_MONITOR}/admin/health`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status}`);
      }

      const data = await response.json();
      // Accept health data from various response shapes
      setHealth(data.health || data || {});
    } catch (err) {
      console.error('[HealthDashboard] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchHealth]);

  if (loading && !health) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="health-dashboard">
      <h2>System Health</h2>

      {error && (
        <div className="admin-alert admin-alert--warning">
          <span>⚠️</span> {error}
          <div className="admin-btn-group">
            <button className="admin-btn admin-btn--sm" onClick={fetchHealth}>
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="admin-grid admin-grid-3">
        <div className="admin-stat-card">
          <div className="admin-stat-icon green">✅</div>
          <div className="admin-stat-value">{health?.uptime || (error ? '—' : '99.9%')}</div>
          <div className="admin-stat-label">Uptime</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon blue">⚡</div>
          <div className="admin-stat-value">{health?.responseTime || (error ? '—' : '120ms')}</div>
          <div className="admin-stat-label">Avg Response Time</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon purple">📊</div>
          <div className="admin-stat-value">{health?.requestsPerMinute || (error ? '—' : '1,234')}</div>
          <div className="admin-stat-label">Requests/Min</div>
        </div>
      </div>

      {health?.services && (
        <div className="admin-card admin-section-gap">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Service Status</h3>
          </div>
          <div className="admin-grid admin-grid-2">
            {Object.entries(health.services).map(([name, status]) => (
              <div key={name} className="admin-stat-card">
                <div className="admin-stat-icon">{status === 'healthy' || status === 'ok' ? '🟢' : status === 'degraded' ? '🟡' : '🔴'}</div>
                <div className="admin-stat-value admin-stat-value--sm">{status}</div>
                <div className="admin-stat-label">{name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!health && !error && !loading && (
        <div className="admin-alert admin-alert--info admin-section-gap">
          <span>ℹ️</span> No health data available. The system monitor may not be reporting yet.
        </div>
      )}
    </div>
  );
};

export default HealthDashboard;
